import React, { useEffect } from 'react';
import FooterOnlyLayout from './layout/FooterOnlyLayout';
import { Form, Checkbox } from 'antd';
import { ValidateErrorEntity } from 'rc-field-form/lib/interface';
import { postData } from '../utils/axiosRequest';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import LanguageSubMenu from '../components/LanguageSubMenu';
import LoginForm, { LoginFormValues } from '../components/LoginForm';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useLogin } from '../hooks/useLogin';
import { useAuth } from '../utils/createAuthProvider';
import { alertMessage } from '../utils/alertMessage';
import { dashboardRoute } from '../constants/pathname';
import { GENERAL_TIMEOUT } from '../constants/systemConstants';
import getDashboardStyle from '../utils/getDashboardStyle';

/**
 * Login Page
 *
 * /auth/login
 *
 * @param route Route information that includes info like title
 */
const Login = () => {
  const { t } = useTranslation();
  const history = useHistory();
  useDocumentTitle(t('general.login'));
  const auth = useAuth();
  const completeLogin = useLogin();

  /**
   * @param values.username Username entered
   * @param values.password Password entered
   * @param values.remember TODO: Should the user be remember after session is over
   */
  const loginHandler = async (values: LoginFormValues) => {
    const response = await postData(
      'auth/login',
      getDashboardStyle().isSalesForceAppType
        ? {
            mobile: values.username,
            password: values.password,
          }
        : {
            username: values.username,
            password: values.password,
          }
    );
    if (response && response.goodStatus && response.data?.accessToken) {
      await completeLogin(response.data.accessToken);
      setTimeout(() => history.push(dashboardRoute.home), GENERAL_TIMEOUT);
    } else {
      alertMessage(
        'error',
        response?.msg || t('general.noResponse'),
        response?.data || undefined
      );
    }
  };

  /**
   * @param errorInfo Information on the failed Login
   */
  const loginFailed = (errorInfo: ValidateErrorEntity) => {
    console.log('Failed:', errorInfo);
  };

  useEffect(() => {
    if (auth.isLogged) {
      history.push(dashboardRoute.home);
    }
    let state = window.history.state;
    if (state && state.returnCode === 401) {
      alertMessage('error', t('login.alerts.loginRequired'));
    }
  }, [history, auth, t]);

  return (
    <FooterOnlyLayout>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '0 16px',
          height: 48,
        }}
      >
        <div style={{ flex: 1 }}></div>
        <LanguageSubMenu />
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div style={{ marginTop: 70 }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img
              src={getDashboardStyle().icon}
              alt="logo"
              style={{ height: 42, width: 42, marginRight: 20 }}
            />
            <h1 style={{ margin: 0, fontSize: 38, fontWeight: 640 }}>
              {t(getDashboardStyle().title)}
            </h1>
          </div>
          <div style={{ marginTop: 30 }}>
            <LoginForm
              onFinish={loginHandler}
              onFinishFailed={loginFailed}
              initialValues={{ remember: true }}
              style={{ width: 320 }}
            >
              <Form.Item
                name="remember"
                valuePropName="checked"
                wrapperCol={{
                  span: 16,
                }}
              >
                <Checkbox>{t('login.rememberMe')}</Checkbox>
              </Form.Item>
            </LoginForm>
          </div>
        </div>
      </div>
    </FooterOnlyLayout>
  );
};

export default Login;
